import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Link,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  Button
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Order } from '../types/order';
import { APIHOST } from '../common';
import { useAuth } from './auth/AuthContext';

interface LiveOrderProps {
  accessCode?: string;
  orderId?: string;
  refreshTrigger?: number;
}

const LiveOrder = ({ orderId, refreshTrigger }: LiveOrderProps) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isTimeLow, setIsTimeLow] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [userDismissedWarning, setUserDismissedWarning] = useState(false);
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const { currentUser } = useAuth();

  const fetchOrder = async () => {
    if (!currentUser) return;
    
    setError(null);
    
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found');
      }

      let url = `${APIHOST}/api/orders`;
      
      if (orderId) {
        // Fetch specific order by ID
        url += `/${orderId}?populate[]=conference_hotel&populate[]=conference_hotel.hotel&populate[]=order_rooms`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order data');
      }

      const result = await response.json();
    
      // Single order response
      setOrder(result.data);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [currentUser, refreshTrigger]);

  useEffect(() => {
    // Set up interval to fetch order every minute
    const interval = setInterval(() => {
      fetchOrder();
    }, 60000); // 60000ms = 1 minute

    return () => clearInterval(interval);
  }, [currentUser, refreshTrigger]); // Only depend on currentUser to avoid recreating interval unnecessarily

  const handlePingOrder = async () => {
    if (!order?.documentId) return;
    setUserDismissedWarning(false); // Reset dismissed warning when pinging
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${order.documentId}/ping`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to ping order');
      }

      const result = await response.json();
      // Update the order with new lastPing timestamp
      setOrder(prev => prev ? { ...prev, lastPing: result.data.lastPing } : null);
    } catch (err) {
      console.error('Error pinging order:', err);
    }
  };

  const handleReviveOrder = async () => {
    if (!order?.documentId) return;
    setUserDismissedWarning(false); // Reset dismissed warning when reviving
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${order.documentId}/revive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revive order');
      }

      // Upon success, refresh the order data
      window.location.reload();
    } catch (err) {
      console.error('Error reviving order:', err);
    }
  };

  useEffect(() => {
    if (!order?.lastPing) return;

    const updateTimer = () => {
      const lastPingTime = new Date(order.lastPing!);
      const expiryTime = new Date(lastPingTime.getTime() + 20 * 60 * 1000); // Add 20 minutes
      const now = new Date();
      const timeDiff = expiryTime.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeRemaining('Expired');
        setIsTimeLow(false);
        return;
      }

      const minutes = Math.floor(timeDiff / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      
      // Set warning if less than 2 minute remaining
      const newIsTimeLow = minutes < 2;
      setIsTimeLow(newIsTimeLow);
      
      // Show warning dialog when time becomes low
      if (newIsTimeLow && !showWarningDialog) {
        setShowWarningDialog(true);
      }
      console.log(`Order timer updated: ${timeRemaining} remaining, isTimeLow: ${newIsTimeLow}`, userDismissedWarning);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [order?.lastPing]);

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} sx={{ ml: 2 }} />
        <Typography variant="body2" component="span">
          Loading order data...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!order) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No active order found
      </Alert>
    );
  }

  const roomCount = order.order_rooms?.length || 0;
  const hotelName = order.conference_hotel?.hotel?.name || 'No hotel selected';
  const isExpired = timeRemaining === 'Expired' || order.orderStatus === 'abandoned';
  
  // Show expired dialog when order becomes expired
  if (isExpired && !showExpiredDialog) {
    setShowExpiredDialog(true);
  }
  

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 2, position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Warning for expiring order */}
      {isTimeLow && !isExpired && (
        <Dialog open={showWarningDialog && !userDismissedWarning} onClose={() => {setShowWarningDialog(false); setUserDismissedWarning(true);}} fullWidth>
          <DialogTitle>
            Order Expiring Soon
            <IconButton
              aria-label="close"
              onClick={() => {setShowWarningDialog(false); setUserDismissedWarning(true);}}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Alert severity="warning" sx={{ mb: 2, m: 2 }}>
            Your order is expiring soon! If you need more time, please click <Link href="#" onClick={handlePingOrder}>here</Link> to refresh your session.
          </Alert>
        </Dialog>
      )}
      {isExpired && (
        <Dialog open={showExpiredDialog} onClose={() => setShowExpiredDialog(false)} fullWidth>
          <DialogTitle>
            Order Expired
            <IconButton
              aria-label="close"
              onClick={() => setShowExpiredDialog(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Alert severity="error" sx={{ p: 5 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Order Timed Out
            </Typography>
            <Typography variant="body1" component="p">
              Your order has expired due to inactivity. Any rooms you had selected have been released.
            </Typography>
            <Typography variant="body1" component="p" sx={{ mt: 2 }}>
              If you wish to try to continue your order, please click the re-open button below. Otherwise, you can start a new order.
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button href="#" onClick={handleReviveOrder}>Re-open order</Button>
              <Button href="/">Start a new order</Button>
            </Box>
          </Alert>
        </Dialog>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Order Status
        </Typography>
        {order.lastPing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={isExpired ? 'Expired' : `Time remaining: ${timeRemaining}`}
              color={isExpired ? 'error' : isTimeLow ? 'warning' : 'primary'}
              size="small"
            />
            <Tooltip 
              title="Orders that expire due to inactivity will have their rooms released for others to book. Complete your registration before the timer expires to secure your reservation. Timer will add more time as you progress."
              placement="left"
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* <Box sx={{ minWidth: 150 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Selected Hotel
          </Typography>
          <Typography variant="body1" component="div" sx={{ fontWeight: 500, pt:1}}>
            {hotelName}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Rooms Selected
          </Typography>
          <Typography variant="h4" component="div" color="primary">
            {roomCount}
          </Typography>
        </Box> */}

      </Box>


    </Paper>
  );
};

export default LiveOrder;